import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, Divider, IconButton, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalText from '../common/LocalText';
import { useToast } from '../../context/ToastContext';
import { getAnnouncementPlayerBreakdown } from '../../api/gameAnnouncementApi';
import { AVATAR_PALETTE, C, btnBase, ctaNavy } from '../../theme/adminTokens';
import { colorForId, initialOf } from '../../utils/avatarColor';
import { formatEt } from '../../utils/formatEt';
import { formatPeriodRange } from '../../utils/gameSchedule';

const headCellSx = {
  fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, '&:last-of-type': { borderRight: 'none' },
  whiteSpace: 'nowrap', py: 1.25, px: 1.5, bgcolor: '#f1f5f9',
};

const bodyCellSx = {
  fontSize: 13, color: '#1e293b', borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' }, py: 1.25, px: 1.5, verticalAlign: 'middle',
};

/**
 * The "Details" of a player of a Quiz announcement: the points the player earned in each of the announcement's
 * selected quizzes (one row per quiz played). A Quiz announcement is scored per quiz, so this replaces the date-based
 * match history the Penalty announcements use.
 */
export default function AnnouncementPlayerBreakdownModal({
  open, onClose, gameId, announcementId, row,
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open || !gameId || !announcementId || !row?.userId) return undefined;
    let cancelled = false;
    setLoading(true);
    getAnnouncementPlayerBreakdown(gameId, announcementId, row.userId)
      .then((data) => { if (!cancelled) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) { setRows([]); toast.error('Failed to load the points per quiz.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gameId, announcementId, row?.userId]);

  if (!row) return null;

  const total = rows.reduce((sum, r) => sum + (r.points ?? 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="announcement-breakdown-title">
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: colorForId(row.userId, AVATAR_PALETTE), color: '#ffffff', fontSize: 18, fontWeight: 700,
            }}
          >
            {initialOf(row.username || row.email)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="announcement-breakdown-title" sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{row.username}</Typography>
            <Typography sx={{ fontSize: 13, color: C.muted }}>{row.email || '—'}</Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b', mb: 1 }}>
          <LocalText>Points per quiz</LocalText>
        </Typography>

        {loading ? (
          <Typography variant="body2" role="status" sx={{ color: C.muted, textAlign: 'center', py: 3 }}>
            <LocalText>Loading…</LocalText>
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" role="status" sx={{ color: C.muted, textAlign: 'center', py: 3 }}>
            <LocalText>This player has no points in the selected quizzes.</LocalText>
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '8px' }}>
            <Table size="small" aria-label="Points per quiz" sx={{ '& tbody tr:last-of-type td': { borderBottom: 'none' } }}>
              <TableHead>
                <TableRow>
                  <TableCell scope="col" sx={headCellSx}><LocalText>Quiz</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx}><LocalText>Period</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx} align="right"><LocalText>Points</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx} align="right"><LocalText>Correct</LocalText></TableCell>
                  <TableCell scope="col" sx={headCellSx}><LocalText>Played at</LocalText></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.gameScheduleId} hover>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell sx={bodyCellSx}>{formatPeriodRange(r.startDate, r.endDate)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">{r.points}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{`${r.correctCount} / ${r.totalQuestions}`}</TableCell>
                    <TableCell sx={bodyCellSx}>{formatEt(r.playedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && rows.length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 1.5 }}>
            <LocalText>Total points</LocalText>: <Box component="strong" sx={{ color: C.navy }}>{total}</Box>
          </Typography>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" size="small" onClick={onClose} sx={{ ...btnBase, ...ctaNavy }}>
            <LocalText>Done</LocalText>
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
